import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ArsService } from '../services/ars.service';
import { CustomerdatabaseService } from '../services/customerdatabase.service';
import { SessionInitService } from '../services/session-init.service';
import {
  OnboardingPartner,
  PARTNER_ONBOARDING_DEFERRED_KEY,
  PARTNER_ONBOARDING_DONE_KEY,
  PARTNER_ONBOARDING_DRAFT_KEY,
  PartnerConnectForm,
  PartnerOnboardingStep,
  PartnerStatus,
  adminStep,
  buildAdminMailto,
  connectFormError,
  connectFormFromStatus,
  isValidAdminEmail,
  partnerErrorMessage,
  partnerFromUrl,
  partnerLegal,
  partnerStorageKey
} from './partner-onboarding';

@Component({
  selector: 'app-partner-onboarding',
  templateUrl: './partner-onboarding.component.html',
  styleUrls: ['./partner-onboarding.component.scss']
})
export class PartnerOnboardingComponent implements OnInit, OnDestroy {
  partner: OnboardingPartner | null = null;
  legal: ReturnType<typeof partnerLegal> | null = null;
  visible = false;
  step: PartnerOnboardingStep = 'role';
  status: PartnerStatus | null = null;
  connectForm: PartnerConnectForm = connectFormFromStatus(null);
  adminEmail = '';
  accepted = false;
  saving = false;
  error = '';
  info = '';

  private routerSub?: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private customerDB: CustomerdatabaseService,
    private sessionInit: SessionInitService,
    private ars: ArsService
  ) {}

  ngOnInit() {
    this.refresh(this.router.url);
    this.routerSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(event => this.refresh(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  async chooseAdmin(isAdmin: boolean) {
    this.error = '';
    this.info = '';
    if (!isAdmin) {
      this.setStep('notify');
      return;
    }
    await this.run(async () => {
      const status = await this.loadStatus();
      const step = adminStep(status);
      if (step === 'notify') {
        this.info = 'Sie sind in MONA nicht als Administrator Ihrer Organisation hinterlegt. Laden Sie Ihren Administrator ein.';
      }
      await this.goTo(step);
    });
  }

  async sendAdminEmail() {
    const partner = this.partner;
    const email = this.adminEmail.trim();
    if (!partner) return;
    if (!isValidAdminEmail(email)) {
      this.error = 'Bitte eine gültige E-Mail-Adresse des Administrators eingeben.';
      return;
    }
    await this.run(async () => {
      const result = await this.ars.partner<{ emailed: boolean; deepLink: string }>(partner.id, 'notifyAdmin', { adminEmail: email });
      if (!result.emailed) {
        window.location.href = buildAdminMailto(partner, email, result.deepLink);
      }
      sessionStorage.setItem(partnerStorageKey(PARTNER_ONBOARDING_DEFERRED_KEY, partner), '1');
      this.visible = false;
    });
  }

  async continueConsent() {
    const step = this.step;
    if (!this.partner || !this.accepted || this.saving || (step !== 'gdpr' && step !== 'dpa' && step !== 'gcc')) return;
    const partnerId = this.partner.id;
    await this.run(async () => {
      await this.ars.partner(partnerId, 'acceptConsent', { document: step });
      this.accepted = false;
      await this.goTo(adminStep(await this.loadStatus()));
    });
  }

  async connect() {
    const partner = this.partner;
    if (!partner) return;
    const formError = connectFormError(partner, this.connectForm, !!this.status?.connected);
    if (formError) {
      this.error = formError;
      return;
    }
    await this.run(async () => {
      await this.ars.partner(partner.id, 'connect', {
        url: this.connectForm.url.trim(),
        username: this.connectForm.username.trim(),
        password: this.connectForm.password,
        statusToPull: this.connectForm.statusToPull
      });
      this.connectForm.password = '';
      await this.goTo('activate');
    });
  }

  back() {
    this.error = '';
    this.info = '';
    this.accepted = false;
    const previous: { [step: string]: PartnerOnboardingStep } = {
      notify: 'role', gdpr: 'role', dpa: 'gdpr', gcc: 'dpa', connect: 'gcc'
    };
    this.setStep(previous[this.step] || 'role');
  }

  private async goTo(step: PartnerOnboardingStep) {
    if (!this.partner) return;
    if (step === 'connect') {
      this.connectForm = connectFormFromStatus(this.status);
    }
    if (step === 'activate') {
      this.setStep('activate');
      await this.ars.partner(this.partner.id, 'activate');
      step = 'done';
    }
    if (step === 'done') {
      localStorage.setItem(partnerStorageKey(PARTNER_ONBOARDING_DONE_KEY, this.partner), '1');
      sessionStorage.removeItem(PARTNER_ONBOARDING_DRAFT_KEY);
      this.visible = false;
      return;
    }
    this.setStep(step);
  }

  private async run(action: () => Promise<void>) {
    this.saving = true;
    this.error = '';
    try {
      await this.ensureSession();
      await action();
    } catch (err) {
      console.error('[PartnerOnboarding] Step failed', this.partner?.id, (err as any)?.status || err);
      this.error = partnerErrorMessage(this.partner, err);
    } finally {
      this.saving = false;
    }
  }

  private async loadStatus(): Promise<PartnerStatus> {
    this.status = await this.ars.partner<PartnerStatus>(this.partner!.id, 'status');
    return this.status;
  }

  private async ensureSession() {
    if (this.customerDB.isDBConnected()) return;
    const user = await this.authService.waitForAuthState();
    if (user) {
      await this.sessionInit.minimalAgentsLogin(user);
    }
  }

  private refresh(url: string) {
    const partner = partnerFromUrl(url);
    if (!partner
      || localStorage.getItem(partnerStorageKey(PARTNER_ONBOARDING_DONE_KEY, partner)) === '1'
      || sessionStorage.getItem(partnerStorageKey(PARTNER_ONBOARDING_DEFERRED_KEY, partner)) === '1') {
      this.visible = false;
      return;
    }
    if (this.partner?.id !== partner.id) {
      this.partner = partner;
      this.legal = partnerLegal(partner);
      this.status = null;
      this.step = this.readDraftStep(partner);
    }
    this.visible = true;
  }

  private setStep(step: PartnerOnboardingStep) {
    this.step = step;
    if (this.partner) {
      sessionStorage.setItem(PARTNER_ONBOARDING_DRAFT_KEY, JSON.stringify({ partner: this.partner.id, step }));
    }
  }

  private readDraftStep(partner: OnboardingPartner): PartnerOnboardingStep {
    try {
      const raw = sessionStorage.getItem(PARTNER_ONBOARDING_DRAFT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const resumable: PartnerOnboardingStep[] = ['role', 'notify', 'gdpr', 'dpa', 'gcc'];
      return parsed && parsed.partner === partner.id && resumable.includes(parsed.step) ? parsed.step : 'role';
    } catch {
      return 'role';
    }
  }
}
